import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";

/**
 * Per-user localStorage persistence for the Curve Management form. Survives
 * page reloads so an in-progress create/edit isn't lost when the user
 * accidentally refreshes or follows a deep link.
 *
 * Keys are scoped by user id so multiple accounts on the same device don't
 * see each other's drafts.
 */

const STORAGE_NAMESPACE = "artis:curve-management:draft";

type DraftRecord = Record<string, unknown>;

function storageKey(userId: string | undefined): string | null {
  if (!userId) return null;
  return `${STORAGE_NAMESPACE}:${userId}`;
}

function safeParse(raw: string | null): DraftRecord {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as DraftRecord) : {};
  } catch {
    return {};
  }
}

export function readDraft(userId: string | undefined): DraftRecord {
  const key = storageKey(userId);
  if (!key || typeof window === "undefined") return {};
  try {
    return safeParse(window.localStorage.getItem(key));
  } catch {
    return {};
  }
}

function writeDraft(userId: string | undefined, draft: DraftRecord): void {
  const key = storageKey(userId);
  if (!key || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(draft));
  } catch {
    // quota exceeded / private mode — silently no-op
  }
}

export function clearDraft(userId: string | undefined): void {
  const key = storageKey(userId);
  if (!key || typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

/**
 * `useState` whose value is mirrored into a per-user localStorage record so
 * the form survives page reloads. Each call mounts with the persisted value
 * (when one exists) and writes back on every change.
 *
 * The value MUST be JSON-serialisable.
 */
export function useDraftState<T>(
  userId: string | undefined,
  field: string,
  initialValue: T | (() => T),
): [T, Dispatch<SetStateAction<T>>] {
  // Read the snapshot once per mount. We deliberately don't reactively watch
  // the storage — `CurveFormState` is the single owner of these fields.
  const initialDraft = useMemo(() => readDraft(userId), [userId]);

  const [value, setValue] = useState<T>(() => {
    if (field in initialDraft) {
      return initialDraft[field] as T;
    }
    return typeof initialValue === "function" ? (initialValue as () => T)() : initialValue;
  });

  useEffect(() => {
    if (!userId) return;
    const draft = readDraft(userId);
    // Only write when the value has actually changed to avoid an infinite
    // loop in the (rare) case the JSON shape happens to round-trip.
    const next = { ...draft, [field]: value };
    writeDraft(userId, next);
  }, [userId, field, value]);

  return [value, setValue];
}
